import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {FormikHelpers} from "formik";
import ContactForm from '../Form';
import {Contact, ContactData} from "../../../types/Contact";
import Button from "../../../components/Button";
import {TrashIcon} from "@heroicons/react/solid";
import {useAuth} from "../../../context/AuthContext";
import {DeleteContact} from "../index";

function MutateContact() {
    const {id} = useParams()
    const {data: contact, isLoading} = useQuery(
        ['contacts', {id}],
        () => ngage.contacts.get(Number(id)),
        {enabled: !!id}
    )

    const store = useMutation((data: ContactData) => ngage.contacts.store(data))
    const update = useMutation((data: ContactData) => ngage.contacts.update(parseInt(id as string), data))

    const {roles, can} = useAuth();
    const {notify} = useNotifications();
    const navigate = useNavigate();

    const [deletingContact, setDeletingContact] = useState<Contact | null>(null);

    // Handle form submission
    async function handleSubmit(values: ContactData, {setSubmitting}: FormikHelpers<ContactData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('contacts.all');
        await invalidate(['contacts', {id}]);

        setSubmitting(false)
        navigate('/contacts')
    }

    // Handle store mutation
    async function handleStore(values: ContactData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Contact Registered", `${values.first_name} has been created`)
        } catch (error) {
            notify("error", "Contact Not Registered", `${values.first_name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: ContactData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Contact Updated", `${values.first_name} has been updated`)
            await invalidate(['users', {id}])
        } catch (error) {
            notify("error", "Contact Not Updated", `${values.first_name} could not be updated`)
            throw error
        }
    }

    // Prepare action handlers
    function handleDeleteContact() {
        if (contact) setDeletingContact(contact)
    }

    // Prepare breadcrumbs
    const breadcrumbs = {
        'Engagement': '/',
        'Contacts': '/contacts',
        [`${id? 'Edit' : 'New'} Contact`]: id? `/contacts/${id}/edit`: '/contacts/new'
    }

    // Prepare page actions
    const actions = <>
        {/* TODO: This is a temporary solution to prevent Audience Managers deleting contacts */}
        {(can('contacts:write') && !roles.includes('audience manager') && id) &&
          <Button
            color={Button.colors.WHITE}
            icon={TrashIcon}
            onClick={handleDeleteContact}
            disabled={!contact}
          >
              Delete
          </Button>
        }
    </>

    return (
        <Page title={`${id? 'Edit' : 'New'} Contact`}
              breadcrumbs={breadcrumbs}
              isLoading={isLoading}
              actions={actions}
        >
            <ContactForm contact={contact} onSubmit={handleSubmit}/>

            {/* Delete confirmation modal */}
            <DeleteContact
              show={contact === deletingContact}
              contact={deletingContact}
              onCancel={() => setDeletingContact(null)}
              onDelete={() => navigate(-1)}
              onDeleteFail={() => setDeletingContact(null)}
            />
        </Page>
    );
}

export default MutateContact;